/**
 * OptInPage.js
 * 
 * Shown to students BEFORE they reach the main dashboard.
 * Lists all exams the student is enrolled in.
 * Student must opt-in to at least one exam before accessing the dashboard.
 * They can opt-out with a reason at any time.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';

// ── Opt-out reason modal ──────────────────────────────────────────────────────
function OptOutModal({ exam, onConfirm, onCancel }) {
  const [reason, setReason] = useState('');
  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 460 }}>
        <h3 className="modal-title">Opt Out — {exam.title}</h3>
        <div className="alert alert-warning" style={{ marginBottom: 16 }}>
          Are you sure you want to opt out of this exam? You can opt back in before the exam starts.
        </div>
        <div className="form-group">
          <label className="form-label">Reason for opting out *</label>
          <textarea
            className="form-input form-textarea"
            style={{ minHeight: 90, resize: 'vertical' }}
            placeholder="Please explain why you are opting out (required)..."
            value={reason}
            onChange={e => setReason(e.target.value)}
            autoFocus
          />
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 16 }}>
          <button className="btn btn-secondary" onClick={onCancel}>Cancel</button>
          <button
            className="btn btn-danger"
            disabled={!reason.trim()}
            onClick={() => reason.trim() && onConfirm(reason.trim())}
          >
            Opt Out
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── OptInPage ────────────────────────────────────────────────────────────────
export default function OptInPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [exams,   setExams]   = useState([]);   // enrolled exams with opt status
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState({});    // examId → true while saving
  const [optOutModal, setOptOutModal] = useState(null); // exam to opt-out from

  // Fetch enrolled exams + opt status for each
  const fetchExams = useCallback(async () => {
    try {
      const res = await axios.get('/api/exams'); // returns enrolled exams
      // For each exam, fetch opt status
      const withOpt = await Promise.all(res.data.map(async (ex) => {
        try {
          const opt = await axios.get(`/api/organizations/exams/${ex.id}/my-opt`);
          return { ...ex, opt_status: opt.data.opt_status || 'in', opt_reason: opt.data.opt_reason };
        } catch {
          return { ...ex, opt_status: 'in', opt_reason: null };
        }
      }));
      setExams(withOpt);
    } catch (err) {
      toast.error('Failed to load exams');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchExams(); }, [fetchExams]);

  // Check if student has at least one opted-in exam → allow proceed
  const hasOptedIn = exams.some(e => e.opt_status === 'in');

  const handleOptIn = async (exam) => {
    setSaving(p => ({ ...p, [exam.id]: true }));
    try {
      await axios.post(`/api/organizations/exams/${exam.id}/opt`, { opt_status: 'in' });
      setExams(p => p.map(e => e.id === exam.id ? { ...e, opt_status: 'in', opt_reason: null } : e));
      toast.success(`✅ Opted in for "${exam.title}"`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to opt in');
    } finally {
      setSaving(p => ({ ...p, [exam.id]: false }));
    }
  };

  const handleOptOut = async (exam, reason) => {
    setSaving(p => ({ ...p, [exam.id]: true }));
    setOptOutModal(null);
    try {
      await axios.post(`/api/organizations/exams/${exam.id}/opt`, { opt_status: 'out', opt_reason: reason });
      setExams(p => p.map(e => e.id === exam.id ? { ...e, opt_status: 'out', opt_reason: reason } : e));
      toast.success(`Opted out of "${exam.title}"`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to opt out');
    } finally {
      setSaving(p => ({ ...p, [exam.id]: false }));
    }
  };

  const proceedToDashboard = () => {
    if (!hasOptedIn) {
      toast.warning('Please opt in to at least one exam before proceeding.');
      return;
    }
    navigate('/dashboard');
  };

  if (loading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'var(--bg-primary)',
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '40px 20px',
    }}>
      <div style={{ width: '100%', maxWidth: 700 }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div className="logo-text" style={{ fontSize: 26, marginBottom: 6 }}>⬡ ExamAI</div>
          <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 8 }}>
            Exam Enrollment
          </h1>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', maxWidth: 500, margin: '0 auto' }}>
            Welcome, <strong>{user?.full_name}</strong>! Below are the exams you have been enrolled in.
            Please opt in to participate or opt out if you do not wish to take an exam.
          </p>
        </div>

        {/* Exam cards */}
        {exams.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: 48 }}>
            <div style={{ fontSize: 40, marginBottom: 14 }}>📋</div>
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>No Exams Found</div>
            <div style={{ color: 'var(--text-secondary)', marginBottom: 24 }}>
              You haven't been enrolled in any exams yet. If you have a registration link, please use it first.
            </div>
            <button className="btn btn-secondary" onClick={() => { logout(); navigate('/login'); }}>
              Back to Login
            </button>
          </div>
        ) : (
          <>
            {exams.map(exam => (
              <div key={exam.id} className="card" style={{
                marginBottom: 16,
                border: `1px solid ${exam.opt_status === 'out' ? 'rgba(239,68,68,0.3)' : 'var(--border)'}`,
                opacity: exam.opt_status === 'out' ? 0.85 : 1,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
                  <div style={{ flex: 1 }}>
                    {/* Exam title + status */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                      <h3 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>{exam.title}</h3>
                      <span className={`badge badge-${exam.opt_status === 'in' ? 'green' : 'red'}`}>
                        {exam.opt_status === 'in' ? '✅ Opted In' : '❌ Opted Out'}
                      </span>
                    </div>

                    {/* Exam metadata */}
                    <div style={{ display: 'flex', gap: 16, fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8, flexWrap: 'wrap' }}>
                      <span>⏱ {exam.duration_minutes} min</span>
                      <span>❓ {exam.question_count} questions</span>
                      <span>🎯 {exam.total_marks} marks (pass: {exam.passing_marks})</span>
                      {exam.proctoring_enabled && <span style={{ color: 'var(--accent-warning)' }}>🔒 AI Proctored</span>}
                    </div>

                    {/* Proctor published status */}
                    {!exam.proctor_published ? (
                      <div style={{ fontSize: 12, color: 'var(--accent-warning)', marginBottom: 8 }}>
                        ⏳ Exam not yet opened by proctor — you'll be notified when it starts
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: 'var(--accent-success)', marginBottom: 8 }}>
                        🟢 Exam is open — you can start once you proceed to the dashboard
                      </div>
                    )}

                    {/* Opt-out reason */}
                    {exam.opt_status === 'out' && exam.opt_reason && (
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic', padding: '6px 10px', background: 'var(--bg-elevated)', borderRadius: 6 }}>
                        Opt-out reason: "{exam.opt_reason}"
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
                    {exam.opt_status === 'out' ? (
                      <button
                        className="btn btn-success btn-sm"
                        disabled={saving[exam.id]}
                        onClick={() => handleOptIn(exam)}
                      >
                        {saving[exam.id] ? '...' : '✅ Opt Back In'}
                      </button>
                    ) : (
                      <button
                        className="btn btn-danger btn-sm"
                        disabled={saving[exam.id]}
                        onClick={() => setOptOutModal(exam)}
                      >
                        {saving[exam.id] ? '...' : '❌ Opt Out'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* Summary + action */}
            <div style={{ padding: '16px 20px', background: 'var(--bg-elevated)', borderRadius: 12, border: '1px solid var(--border)', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontSize: 14 }}>
                  <span style={{ color: 'var(--accent-success)', fontWeight: 600 }}>
                    {exams.filter(e => e.opt_status === 'in').length} opted in
                  </span>
                  {' · '}
                  <span style={{ color: 'var(--accent-danger)' }}>
                    {exams.filter(e => e.opt_status === 'out').length} opted out
                  </span>
                  {' of '}
                  <span style={{ fontWeight: 600 }}>{exams.length} exams</span>
                </div>
                {!hasOptedIn && (
                  <div style={{ fontSize: 12, color: 'var(--accent-warning)' }}>
                    ⚠ Opt in to at least one exam to proceed
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => { logout(); navigate('/login'); }}>
                Logout
              </button>
              <button
                className="btn btn-primary btn-lg"
                disabled={!hasOptedIn}
                onClick={proceedToDashboard}
              >
                Proceed to Dashboard →
              </button>
            </div>
          </>
        )}
      </div>

      {/* Opt-out reason modal */}
      {optOutModal && (
        <OptOutModal
          exam={optOutModal}
          onConfirm={(reason) => handleOptOut(optOutModal, reason)}
          onCancel={() => setOptOutModal(null)}
        />
      )}
    </div>
  );
}
